import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { setToken } from '../utils/auth';
import { useAppContext } from '../contexts/AppContext';

/* ── tiny reusable field ── */
const Field = ({ label, type, placeholder, value, onChange, autoFocus }) => (
  <div style={{ marginBottom: 11 }}>
    <label style={s.label}>{label}</label>
    <input
      style={s.input}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      required
      autoFocus={autoFocus}
      autoCorrect="off"
      autoCapitalize="none"
      spellCheck={false}
      onFocus={e => Object.assign(e.target.style, s.inputFocus)}
      onBlur={e => Object.assign(e.target.style, s.inputBlur)}
    />
  </div>
);

/* ── single login card ── */
const Card = ({ accent, badge, title, subtitle, hint, error, loading, onSubmit, children }) => (
  <div style={{ ...s.card, '--accent': accent }}>
    <div style={{ ...s.cardStripe, background: accent }} />
    <div style={s.cardBody}>
      <div style={s.cardHead}>
        <div style={{ ...s.badge, background: accent + '22', border: `1.5px solid ${accent}44` }}>
          <span style={{ fontSize: 22 }}>{badge}</span>
        </div>
        <div>
          <div style={s.cardTitle}>{title}</div>
          <div style={s.cardSub}>{subtitle}</div>
        </div>
      </div>

      {error && (
        <div style={s.errorBox}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="#dc2626" style={{ flexShrink: 0, marginTop: 1 }}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={onSubmit}>
        {children}
        <button
          type="submit"
          disabled={loading}
          style={{ ...s.submitBtn, background: loading ? '#94a3b8' : accent, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <span style={s.spinner} /> Signing in…
            </span>
          ) : 'Sign in'}
        </button>
      </form>

      {hint && (
        <div style={{ ...s.hintBox, borderColor: accent + '33', background: accent + '0d' }}>
          <svg width="13" height="13" viewBox="0 0 20 20" fill={accent} style={{ flexShrink: 0, marginTop: 1 }}>
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.55 }}>{hint}</span>
        </div>
      )}
    </div>
  </div>
);

/* ── main page ── */
const Login = () => {
  const navigate     = useNavigate();
  const { login }    = useAppContext();

  const [adminEmail,    setAdminEmail]    = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError,    setAdminError]    = useState('');
  const [adminLoading,  setAdminLoading]  = useState(false);

  const handleAdminLogin = async e => {
    e.preventDefault();
    setAdminError('');
    setAdminLoading(true);
    try {
      const res = await apiEndpoints.auth.login({ email: adminEmail, password: adminPassword });
      setToken(res.data.token);
      login(res.data.token);
      navigate('/');
    } catch (err) {
      setAdminError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.wrapper}>
        {/* banner */}
        <img src="/logo-login.png" alt="Fleet OS" style={{ width: '100%', height: 120, objectFit: 'contain', display: 'block', marginBottom: 20 }} />

        {/* cards row */}
        <div style={s.cardsRow} className="login-cards-row">
          <Card
            accent="#0077b6"
            badge="🛡️"
            title="Admin Login"
            subtitle="Full system access & management"
            error={adminError}
            loading={adminLoading}
            onSubmit={handleAdminLogin}
          >
            <Field label="Email address" type="email" placeholder="admin@example.com" value={adminEmail} onChange={setAdminEmail} />
            <Field label="Password" type="password" placeholder="••••••••" value={adminPassword} onChange={setAdminPassword} />
          </Card>

        </div>

        <div style={s.footer}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="#9ca3af" style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Secured with JWT · All sessions are encrypted
        </div>

        <div style={{ marginTop: 14, fontSize: 12, color: '#9ca3af' }}>
          Are you a driver?{' '}
          <Link to="/driver-login" style={{ color: '#059669', fontWeight: 600, textDecoration: 'none' }}>
            Driver login →
          </Link>
        </div>
      </div>

      <style>{`@keyframes login-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    padding: '20px 20px',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    overflowY: 'auto',
    position: 'relative',
  },
  blob: {
    position: 'fixed',
    width: 480,
    height: 480,
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  wrapper: {
    width: '100%',
    maxWidth: 440,
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardsRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
    width: '100%',
    background: '#ffffff',
    borderRadius: 20,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
  },
  card: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  cardStripe: {
    height: 4,
    flexShrink: 0,
  },
  cardBody: {
    flex: 1,
    padding: '22px 28px 26px',
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
  },
  badge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#0d1117',
    letterSpacing: -0.3,
    lineHeight: 1.2,
  },
  cardSub: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 500,
    marginTop: 2,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#374151',
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 8,
    fontSize: 13,
    color: '#0d1117',
    background: '#f9fafb',
    outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  inputFocus: {
    borderColor: '#0077b6',
    background: '#ffffff',
    boxShadow: '0 0 0 3px rgba(0,119,182,0.12)',
  },
  inputBlur: {
    borderColor: '#e5e7eb',
    background: '#f9fafb',
    boxShadow: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '11px',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    marginTop: 4,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'opacity 0.15s, transform 0.12s',
    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'login-spin 0.7s linear infinite',
  },
  hintBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    border: '1px solid',
    borderRadius: 7,
    padding: '8px 10px',
    marginTop: 10,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    background: '#fff1f2',
    border: '1px solid #fecdd3',
    color: '#dc2626',
    borderRadius: 8,
    padding: '9px 13px',
    fontSize: 13,
    marginBottom: 16,
    lineHeight: 1.4,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 14,
    fontSize: 11,
    color: '#9ca3af',
    fontWeight: 500,
  },
};

export default Login;
